/**
 * Structured Data (JSON-LD) helpers.
 *
 * Emits schema.org markup inside <Helmet> so search engines can build rich
 * cards (article snippets, breadcrumbs, product ratings, sitelinks). This
 * bumps CTR in SERPs by ~15–30% for the same rank without any extra content.
 *
 * Usage:
 *   <OrganizationSchema />   // once, at App level
 *   <ArticleSchema article={post} kind="BlogPosting" />
 *   <ArticleSchema article={article} kind="NewsArticle" />
 *   <ProductSchema offer={offer} />
 *   <BreadcrumbSchema items={[{name, url}, ...]} />
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.onlinebusiness.co.il';
const ORG_NAME = 'המרכז הישראלי לעצמאות דיגיטלית';
const ORG_LOGO = `${BASE_URL}/og-image.png`;

const abs = (u) => (!u ? '' : /^https?:\/\//i.test(u) ? u : `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`);

const emit = (data) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

/** Organization + WebSite schema — emit ONCE at the app level so every page
 *  inherits it. Google uses this for the site-name knowledge panel + the
 *  sitelinks search box. */
export const OrganizationSchema = () => emit([
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: ORG_NAME,
    alternateName: 'onlinebusiness.co.il',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: ORG_LOGO,
      width: 1200,
      height: 630,
    },
    sameAs: [
      'https://www.facebook.com/profile.php?id=61568896801638',
      'https://www.instagram.com/affiliate_academy_il/',
    ],
    description: 'הפכו את הידע שלכם בשיווק דיגיטלי לנכסים מניבים. מועדון משווקים מקצועי, הצעות אפיליאייט בלעדיות, וליווי אישי לבניית הכנסה עצמאית.',
    founder: [
      { '@type': 'Person', name: 'רז בליזובסקי' },
      { '@type': 'Person', name: 'אסי אפשטיין' },
    ],
    areaServed: { '@type': 'Country', name: 'Israel' },
    inLanguage: 'he-IL',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: ORG_NAME,
    inLanguage: 'he-IL',
    publisher: { '@id': `${BASE_URL}/#organization` },
  },
]);

/** Article schema — variant works for BlogPosting AND NewsArticle. Google
 *  displays the datePublished/author/image trio when this validates. */
export const ArticleSchema = ({ article, kind = 'BlogPosting', path }) => {
  if (!article) return null;
  const headline = article.headline || article.title || article.primary_title || '';
  const image = article.cover_image || article.thumbnail || article.image;
  const url = `${BASE_URL}${path}`;
  return emit({
    '@context': 'https://schema.org',
    '@type': kind,
    headline,
    description: article.seo_description || article.subtitle || article.excerpt || '',
    image: image ? [abs(image)] : undefined,
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.published_at || article.created_at,
    author: {
      '@type': 'Person',
      name: article.author_name || 'רז בליזובסקי',
    },
    publisher: {
      '@type': 'Organization',
      name: ORG_NAME,
      logo: { '@type': 'ImageObject', url: ORG_LOGO },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: 'he-IL',
  });
};

/** Product schema — used for the affiliate-offer landing pages. Google's rich
 *  result requires `offers` when `@type` is Product; we mark the deal as free
 *  to join (the affiliate signs up, not pays). */
export const ProductSchema = ({ offer }) => {
  if (!offer) return null;
  const path = `/offers/${offer.slug}`;
  return emit({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: offer.title,
    description: offer.tagline || offer.hero_intro || '',
    image: offer.hero_image ? [abs(offer.hero_image)] : undefined,
    brand: { '@type': 'Brand', name: ORG_NAME },
    category: 'Affiliate Marketing Offer',
    url: `${BASE_URL}${path}`,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'ILS',
      price: '0',
      availability: 'https://schema.org/InStock',
      url: `${BASE_URL}${path}`,
    },
  });
};

/** BreadcrumbList — improves SERP appearance with the breadcrumb trail
 *  instead of the raw URL. `items` = array of { name, path }. */
export const BreadcrumbSchema = ({ items = [] }) => {
  if (!items.length) return null;
  return emit({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${BASE_URL}${it.path}`,
    })),
  });
};
